import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { Context as ReactContext, useContext, useEffect, useState } from 'react';

/**
 * The Gold domains an entity may route to in an environment, from that environment's
 * GoldDomainTargets configuration document. Empty while loading, when none are configured, or when
 * the call fails (`hasError`): callers must then accept no domain at all, never assume HCM.
 */
export function useGetGoldDomains(environment?: number) {
  const { httpClient } = useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const [goldDomains, setGoldDomains] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    if (environment == null) {
      setGoldDomains([]);
      setHasError(false);
      setIsLoading(false);
      return undefined;
    }
    let current = true;
    setIsLoading(true);
    httpClient
      .get<string[]>(`${__API_BASE_URL__}/tenant/getGoldDomains?environment=${environment}`, {
        resource: __CLIENT_ID__,
      })
      .then(({ data }) => {
        if (current) {
          setGoldDomains(Array.isArray(data) ? data : []);
          setHasError(false);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (current) {
          setGoldDomains([]);
          setHasError(true);
          setIsLoading(false);
        }
      });
    return () => {
      current = false;
    };
  }, [httpClient, environment]);

  return [goldDomains, isLoading, hasError] as const;
}

/** Why `domain` cannot be submitted, or '' when it is one of the configured Gold domains. */
export function goldDomainError(domain: string | undefined, goldDomains: string[], isLoading: boolean, hasError: boolean): string {
  if (isLoading) {
    return 'Loading the configured Gold domains.';
  }
  if (hasError) {
    return 'The configured Gold domains could not be loaded, so no domain can be selected.';
  }
  if (goldDomains.length === 0) {
    return 'No Gold domains are configured for this environment.';
  }
  if (!domain) {
    return 'Select a Gold domain before submitting.';
  }
  if (!goldDomains.includes(domain)) {
    return `Domain '${domain}' is not configured for this environment; choose one of: ${goldDomains.join(', ')}.`;
  }
  return '';
}
